import { CommonModule } from '@angular/common';
import { Component, OnInit } from '@angular/core';
import { FormsModule, ReactiveFormsModule } from '@angular/forms';
import { DefaultUrlSerializer, PRIMARY_OUTLET, Router, UrlSegment, UrlTree } from '@angular/router';
import { NgbModal } from '@ng-bootstrap/ng-bootstrap';
import { TranslateModule, TranslateService } from '@ngx-translate/core';
import { forkJoin, NEVER, Observable } from 'rxjs';
import { catchError, filter, map, mergeMap, switchMap, tap } from 'rxjs/operators';
import { DeleteAssessmentModalComponent } from '../../dialogs/delete-assessment-modal';
import { RestartAssessmentModalComponent } from '../../dialogs/restart-assessment-modal';
import { AssessmentProductView, AssessmentState, AssessmentWrapperModel } from '../../models/assessment-wrapper.model';
import { KbProductList } from '../../models/kb-product-list.model';
import { AppConfigService } from '../../services/app-config-service/app-config.service';
import { AssessmentService } from '../../services/assessment-service/assessment.service';
import { AuthenticationService } from '../../services/authentication-service/authentication.service';
import { ResourceService } from '../../services/resource-service/resource.service';
import { UserContextService } from '../../services/user-context-service/user-context.service';

@Component({
  selector: 'app-initial-information',
  templateUrl: './initial-information.component.html',
  imports: [TranslateModule, CommonModule, FormsModule, ReactiveFormsModule],
})
export class InitialInformationComponent implements OnInit {
  rspInitialInformationTitle = 'Initial information';
  rspInitialInformationPrepare = 'Create';
  kbProductList: KbProductList = { products: [] };

  internalPrepareAllowed: boolean;
  isDisableable: boolean;
  addAssessmentEnabled: boolean = false;
  hideRiders: boolean;
  isMultipleInsuredPersonsAllowed: boolean;
  insuredPersonNameInput: string;

  constructor(
    private readonly resourceService: ResourceService,
    protected readonly userContextService: UserContextService,
    private readonly assessmentService: AssessmentService,
    private readonly router: Router,
    private readonly modalService: NgbModal,
    private readonly translateService: TranslateService,
    private readonly authService: AuthenticationService,
    private readonly appConfigService: AppConfigService
  ) {}

  ngOnInit(): void {
    this.isDisableable = this.appConfigService.configData?.rspButtonShouldBeDisabled ?? true;
    this.internalPrepareAllowed = !!this.appConfigService.featureToggles?.internalPrepareFeatureToggle;
    this.hideRiders = !!this.appConfigService.featureToggles?.hideRiders;
    this.isMultipleInsuredPersonsAllowed = !!this.appConfigService.featureToggles?.multiAssessmentsAllowed;
    this.resourceService
      .getProductSetup(this.translateService.currentLang)
      .pipe(
        tap(productList => (this.kbProductList = productList)),
        mergeMap(() => this.handleProductViewsForAssessmentWrapper())
      )
      .subscribe();
  }

  prepareAssessment(): void {
    if (!this.addAssessmentEnabled) {
      return;
    }
    this.userContextService
      .internalPrepare(this.userContextService.loggedInUser.id, this.kbProductList, this.insuredPersonNameInput)
      .pipe(
        tap(() => {
          this.addAssessmentEnabled = false;
          this.insuredPersonNameInput = null;
          this.uncheckAllProductBoxes();
        }),
        mergeMap(() => this.handleProductViewsForAssessmentWrapper())
      )
      .subscribe();
  }

  deleteAssessment(assessmentWrapperId: string): void {
    this.modalService
      .open(DeleteAssessmentModalComponent, {
        ariaLabelledBy: 'delete-assessment-title',
        ariaDescribedBy: 'delete-assessment-body',
        centered: true,
      })
      .closed.pipe(
        filter(confirmAction => confirmAction === 'delete'),
        mergeMap(() => this.assessmentService.delete(assessmentWrapperId)),
        mergeMap(() => this.userContextService.get()),
        tap(() => {
          this.addAssessmentEnabled = false;
          this.uncheckAllProductBoxes();
        }),
        mergeMap(() => this.handleProductViewsForAssessmentWrapper())
      )
      .subscribe();
  }

  starRestartOrInitializeAssessment(assessmentWrapperId: string): void {
    this.assessmentService
      .get(assessmentWrapperId)
      .pipe(
        switchMap(wrapper => {
          switch (wrapper.state) {
            case AssessmentState.PREPARED:
            case AssessmentState.STARTED: {
              if (wrapper.directUrl === null) {
                return this.startAssessmentAndNavigate(assessmentWrapperId);
              } else if (wrapper.assessmentLanguage !== this.translateService.currentLang) {
                return this.showLanguageSwitchModalAndNavigate(assessmentWrapperId, this.translateService.currentLang);
              } else {
                return this.authService.refreshCepToken(wrapper.assessmentId).pipe(
                  tap(() => {
                    this.navigateToAssessment(wrapper.directUrl, assessmentWrapperId);
                  })
                );
              }
            }
            case AssessmentState.COMPLETED: {
              return this.assessmentService.isAssessmentCompleted(wrapper.id).pipe(
                switchMap(assessmentComplete => {
                  if (assessmentComplete.isComplete) {
                    this.assessmentService.setCurrentAssessmentWrapperId(assessmentWrapperId);
                    return this.router.navigate(['/confirm', wrapper.assessmentId, { lang: wrapper.assessmentLanguage }]);
                  } else {
                    return this.authService.refreshCepToken(wrapper.assessmentId).pipe(
                      tap(() => {
                        this.navigateToAssessment(wrapper.directUrl, assessmentWrapperId);
                      })
                    );
                  }
                })
              );
            }
            case AssessmentState.CONFIRMED: {
              if (this.appConfigService.featureToggles?.showSigningPage && this.appConfigService.featureToggles?.documentCreationEnabled) {
                this.assessmentService.setCurrentAssessmentWrapperId(assessmentWrapperId);
                return this.router.navigate(['/sign', wrapper.assessmentId, { lang: wrapper.assessmentLanguage }]);
              } else {
                this.viewResult(assessmentWrapperId);
              }
            }
          }
        }),
        catchError(err => {
          if (err.message === 'rspErrorAssessmentNotFound') {
            return this.startAssessmentAndNavigate(assessmentWrapperId);
          }
          return this.router.navigate(['/error']);
        })
      )
      .subscribe();
  }

  private startAssessmentAndNavigate(assessmentWrapperId: string): Observable<void> {
    return this.assessmentService
      .start(assessmentWrapperId)
      .pipe(map(info => this.navigateToAssessment(info.directUrl, assessmentWrapperId)));
  }

  private reStartAssessmentWithLanguage(assessmentWrapperId: string, language: string): Observable<string> {
    return this.assessmentService.restart(assessmentWrapperId, language).pipe(map(info => info.directUrl));
  }

  private showLanguageSwitchModalAndNavigate(assessmentWrapperId: string, language: string): Observable<string> {
    return this.modalService
      .open(RestartAssessmentModalComponent, {
        ariaLabelledBy: 'restart-assessment-title',
        ariaDescribedBy: 'restart-assessment-body',
        centered: true,
      })
      .closed.pipe(
        switchMap(result => {
          if (result === 'restart') {
            return this.reStartAssessmentWithLanguage(assessmentWrapperId, language);
          } else {
            return NEVER;
          }
        })
      )
      .pipe(
        tap(url => {
          this.navigateToAssessment(url, assessmentWrapperId);
        })
      );
  }

  private navigateToAssessment(assessmentUrl: string, assessmentWrapperId: string): void {
    let url: string = assessmentUrl;
    if (url.startsWith('#!')) {
      url = url.replace('#!', '');
    }
    const tree: UrlTree = new DefaultUrlSerializer().parse(url);
    const urlSegments: UrlSegment[] = tree.root.children[PRIMARY_OUTLET].segments;

    if (urlSegments.length === 2 && 'assessment' === urlSegments[0].path) {
      this.assessmentService.setCurrentAssessmentWrapperId(assessmentWrapperId);
      this.router.navigate(['/assessment', urlSegments[1].path, tree.queryParams]);
    }
  }

  viewResult(assessmentWrapperId: string): void {
    this.assessmentService.setCurrentAssessmentWrapperId(assessmentWrapperId);
    this.router.navigate(['/result']);
  }

  validateProductSelection(): void {
    this.addAssessmentEnabled = false;

    this.kbProductList.products.forEach(product => {
      if (product.checked) {
        product.riders?.forEach(rider => {
          if (rider.mandatory) {
            rider.checked = true;
            rider.notAvailable = true;
          }
        });

        this.addAssessmentEnabled = true;
      } else {
        product.riders?.forEach(rider => {
          rider.checked = false;
          rider.notAvailable = false;
        });
      }
    });
  }

  isAssessed(assessmentWrapper: AssessmentWrapperModel): boolean {
    return (
      assessmentWrapper.state === AssessmentState.SIGNED ||
      assessmentWrapper.state === AssessmentState.PROCESSED_MI ||
      assessmentWrapper.state === AssessmentState.PROCESSED
    );
  }

  private uncheckAllProductBoxes(): void {
    this.kbProductList.products.forEach(product => {
      product.checked = false;
      product.riders?.forEach(rider => (rider.checked = false));
    });
  }

  private handleProductViewsForAssessmentWrapper(): Observable<AssessmentProductView[][]> {
    const observables: Observable<AssessmentProductView[]>[] = [];

    this.kbProductList.products.forEach(product => {
      product.notAvailable = false;
    });

    this.userContextService.loggedInUser.assessmentWrappers.forEach(wrapper => {
      observables.push(this.assessmentService.getProductViews(wrapper.id).pipe(tap(productViews => (wrapper.productViews = productViews))));
    });

    return forkJoin(observables).pipe(
      tap((productViews: Array<AssessmentProductView[]>) => this.disableAlreadyChosenProducts(productViews))
    );
  }

  private disableAlreadyChosenProducts(productViewsList: Array<AssessmentProductView[]>): void {
    const createdProductAssessments = [];
    productViewsList.forEach(productViews => {
      productViews.forEach(singleProduct => createdProductAssessments.push(singleProduct.id));
    });

    this.kbProductList.products.forEach(product => {
      product.notAvailable = createdProductAssessments.indexOf(product.id) > -1;
    });
  }
}
